import React, {
  RefCallback,
  useCallback,
  useRef,
  useState,
  useMemo,
  CSSProperties,
} from 'react';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';

import { ButtonBase, buttonShapeMap, buttonSizeMap, Ink } from './ButtonStyle';
import Color from '@styles/Color';
import CircleSpinner from '@components/dataDisplay/spinners/CircleSpinner';
import Icon from '@components/dataDisplay/Icons/Icon';
import icons from '@components/dataDisplay/Icons/Icons';
import { homeSettingState } from '@atom/setting/homeSettingState';
import { getButtonDesignComponent } from './ButtonDesigns';

interface ButtonProps {
  children: any;
  size?: keyof typeof buttonSizeMap;
  shape?: keyof typeof buttonShapeMap;
  color?: 'default' | 'primary' | 'warning';
  pending?: boolean;
  block?: boolean;
  disabled?: boolean;
  ripple?: boolean;
  onClick?: (e) => any;
  icon?: keyof typeof icons | false;
  borderRaius?: number;
  backgroundColor?: string;
  backgroundColorSolid?: string;
  style?: CSSProperties;
  [propName: string]: any;
}

const IconSec = styled.div<{ $noMargin?: boolean }>`
  margin-right: ${({ $noMargin }) => ($noMargin ? '0' : '8px')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 커스텀 디자인 버튼 내부에서 아이콘 + 텍스트를 가로로 배치
const DesignContentRow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

function Button({
  children,
  size = 'md',
  color = 'default',
  shape = 'filled',
  pending,
  block,
  disabled,
  onClick,
  icon,
  ripple = true,
  borderRaius,
  backgroundColor,
  backgroundColorSolid,
  style,
}: ButtonProps) {
  const inkRef = useRef<HTMLDivElement>(null);
  const setRef: RefCallback<HTMLElement | null> = useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        setButtonNode(node);
      }
    },
    [],
  );
  const [buttonNode, setButtonNode] = useState<HTMLElement | null>(null);

  // 버튼 디자인 설정 읽기
  const homeSetting = useRecoilValue(homeSettingState);
  const buttonDesign = homeSetting?.buttonDesign;

  // size='md' 일 때 커스텀 디자인 적용 (shape 무관)
  const DesignComponent = useMemo(() => {
    if (size !== 'md' || !buttonDesign?.design) return null;
    return getButtonDesignComponent(buttonDesign.design);
  }, [size, buttonDesign?.design]);
  const isDesignButton = !!DesignComponent;

  const handleClick = useCallback(
    (e) => {
      if (onClick) onClick(e);
    },
    [onClick],
  );

  const createRipple = useCallback(
    (e) => {
      const ink = inkRef.current;

      if (ripple && ink && buttonNode) {
        ink.classList.remove('animate');
        const diameter = Math.max(
          buttonNode.offsetWidth,
          buttonNode.offsetHeight,
        );
        const mouse_x = e.nativeEvent.offsetX - diameter / 2;
        const mouse_y = e.nativeEvent.offsetY - diameter / 2;
        ink.style.top = mouse_y + 'px';
        ink.style.left = mouse_x + 'px';
        ink.style.width = diameter + 'px';
        ink.style.height = diameter + 'px';
        ink.classList.add('animate');
      }
    },
    [ripple, buttonNode],
  );

  const buttonContent = (
    <>
      {icon && (
        <IconSec>
          <Icon
            name={icon}
            color={
              shape === 'stroke'
                ? 'var(--textColor)'
                : isDesignButton || shape === 'filled'
                  ? 'var(--buttonTextColor)'
                  : 'var(--textColor)'
            }
          />
        </IconSec>
      )}
      {pending && (
        <IconSec $noMargin>
          <CircleSpinner color={color} size={size} shape={shape} />
        </IconSec>
      )}
      {!pending && children}
      <Ink ref={inkRef} />
    </>
  );

  // 커스텀 디자인 렌더링 (shape=filled, size=md)
  if (DesignComponent) {
    const warningBg = color === 'warning' ? Color.RED_6 : undefined;
    const warningColor = color === 'warning' ? Color.WHITE : undefined;
    const resolvedDesignBgColor =
      shape === 'stroke'
        ? backgroundColorSolid || warningBg || 'var(--cardBackgroundColor)'
        : warningBg || buttonDesign!.backgroundColor || undefined;
    return (
      <DesignComponent
        color={
          shape === 'stroke'
            ? 'var(--textColor)'
            : warningColor || buttonDesign!.color || undefined
        }
        backgroundColor={resolvedDesignBgColor}
        pointColor={buttonDesign!.pointColor || undefined}
        onClick={handleClick}
        onMouseDown={createRipple}
        disabled={pending || disabled}
        ref={setRef}
        block={block}
        style={block ? { width: '100%', ...style } : style}
      >
        {/* 아이콘이 있을 때 가로 배치를 보장하는 래퍼 */}
        <DesignContentRow>{buttonContent}</DesignContentRow>
      </DesignComponent>
    );
  }

  return (
    <>
      <ButtonBase
        size={size}
        color={color}
        block={block}
        shape={shape}
        disabled={pending || disabled}
        onClick={handleClick}
        onMouseDown={createRipple}
        ref={setRef}
        style={{
          borderRadius: borderRaius || 'var(--buttonBorderRadius)',
          ...(shape === 'stroke' && backgroundColorSolid
            ? { backgroundColor: backgroundColorSolid }
            : {}),
          ...{ ['cornerShape' as any]: 'var(--buttonCornerShape)' },
          borderImageSource: 'var(--buttonBorderImgURL)',
          borderImageSlice: '30 fill',
          borderImageRepeat: 'stretch',
          borderWidth: 'var(--buttonImgBorderWidth, 0px)',
          borderStyle: 'solid',
          ...style,
        }}
      >
        {buttonContent}
      </ButtonBase>
    </>
  );
}

export default Button;
